import { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * Modern CarouselSlide Component - Enhanced UX/UI
 * @param {Array} data - รูปแบบข้อมูล: ["url,คลิก-path-id", "url,ทั่วไป"]
 */
const CarouselSlide = ({ data = [] }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const slideInterval = 6000; // 6 วินาที - เหมาะสมสำหรับการอ่าน

    const handleNext = useCallback(() => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setActiveIndex((prev) => (prev === data.length - 1 ? 0 : prev + 1));
        setTimeout(() => setIsTransitioning(false), 1000);
    }, [data.length, isTransitioning]);

    const handlePrev = useCallback(() => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setActiveIndex((prev) => (prev === 0 ? data.length - 1 : prev - 1));
        setTimeout(() => setIsTransitioning(false), 1000);
    }, [data.length, isTransitioning]);

    // Auto Play Logic
    useEffect(() => {
        if (data.length <= 1 || isHovered) return;
        
        const interval = setInterval(handleNext, slideInterval);
        return () => clearInterval(interval);
    }, [handleNext, data.length, isHovered]);

    if (!data || data.length === 0) {
        return (
            <div className="w-full aspect-[16/9] bg-gradient-to-br from-slate-100 to-slate-200 animate-pulse rounded-3xl flex items-center justify-center text-slate-400 font-bold shadow-inner">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-300 animate-pulse"></div>
                    <p>กำลังโหลดข้อมูล...</p>
                </div>
            </div>
        );
    }

    return (
        <section className="relative w-full px-2 sm:px-4 lg:px-8 py-4 sm:py-8 lg:py-16">
            <div className="mx-auto max-w-7xl">
                <div
                    className="relative w-full group overflow-hidden rounded-2xl sm:rounded-3xl lg:rounded-[2.5rem] shadow-2xl shadow-blue-900/20 border-2 sm:border-4 border-white/80 hover:border-white transition-all duration-500"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Wrapper สำหรับ Slides - Aspect ratio ตามรูปจริง 10247:3997 ≈ 256:100 */}
                    <div className="relative w-full aspect-[256/100] sm:aspect-[256/100] overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300">
                        {data.map((slide, index) => {
                            const [imgUrl, actionStr] = slide.split(",");
                            const isClickable = actionStr?.split("-")[0] === "คลิก";
                            const linkPath = isClickable ? `${actionStr.split("-")[1]}-${actionStr.split("-")[2]}` : null;

                            return (
                                <div
                                    key={index}
                                    className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out ${
                                        activeIndex === index 
                                            ? "opacity-100 scale-100 translate-x-0 z-10" 
                                            : "opacity-0 scale-105 translate-x-full pointer-events-none z-0"
                                    }`}
                                >
                                    {isClickable ? (
                                        <a 
                                            href={linkPath} 
                                            target="_blank" 
                                            rel="noreferrer noopener" 
                                            className="block w-full h-full relative group/img"
                                            aria-label={`ดูรายละเอียดเพิ่มเติม สไลด์ ${index + 1}`}
                                        >
                                            <img 
                                                src={imgUrl} 
                                                className="w-full h-full object-cover transition-transform duration-[3000ms] group-hover/img:scale-110" 
                                                alt={`ราชพฤกษ์ไตเทียม - ข่าวสารและกิจกรรม ${index + 1}`}
                                                loading={index === 0 ? "eager" : "lazy"}
                                            />
                                            {/* Overlay เมื่อ Hover */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-transparent to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity duration-500" />
                                            
                                            {/* Click Indicator */}
                                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover/img:opacity-100 transition-all duration-300 transform group-hover/img:scale-110">
                                                <div className="bg-white/95 backdrop-blur-md px-8 py-4 rounded-2xl shadow-2xl border-2 border-blue-500">
                                                    <p className="text-blue-900 font-black text-lg flex items-center gap-2">
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                        คลิกเพื่อดูรายละเอียด
                                                    </p>
                                                </div>
                                            </div>
                                        </a>
                                    ) : (
                                        <img 
                                            src={imgUrl} 
                                            className="w-full h-full object-cover" 
                                            alt={`ราชพฤกษ์ไตเทียม - ภาพกิจกรรม ${index + 1}`}
                                            loading={index === 0 ? "eager" : "lazy"}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Navigation Buttons - Modern Glassmorphism Design - Hidden on Mobile */}
                    <div className="hidden md:flex absolute inset-0 items-center justify-between px-4 md:px-8 pointer-events-none z-20">
                        <button
                            onClick={handlePrev}
                            disabled={isTransitioning}
                            className="pointer-events-auto p-3 md:p-4 rounded-2xl bg-white/30 backdrop-blur-xl text-white border-2 border-white/40 shadow-2xl hover:bg-blue-600 hover:border-blue-500 hover:shadow-blue-500/50 transition-all duration-300 -translate-x-16 group-hover:translate-x-0 opacity-0 group-hover:opacity-100 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="สไลด์ก่อนหน้า"
                        >
                            <ChevronLeftIcon className="w-6 h-6 md:w-7 md:h-7 stroke-[3px]" />
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={isTransitioning}
                            className="pointer-events-auto p-3 md:p-4 rounded-2xl bg-white/30 backdrop-blur-xl text-white border-2 border-white/40 shadow-2xl hover:bg-orange-600 hover:border-orange-500 hover:shadow-orange-500/50 transition-all duration-300 translate-x-16 group-hover:translate-x-0 opacity-0 group-hover:opacity-100 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="สไลด์ถัดไป"
                        >
                            <ChevronRightIcon className="w-6 h-6 md:w-7 md:h-7 stroke-[3px]" />
                        </button>
                    </div>

                    {/* Indicators - Modern Progress Dots - Hidden on Mobile */}
                    <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 items-center gap-2.5 px-5 py-3 rounded-full bg-black/20 backdrop-blur-md border border-white/20 shadow-xl z-20">
                        {data.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => !isTransitioning && setActiveIndex(i)}
                                disabled={isTransitioning}
                                className={`transition-all duration-500 rounded-full ${
                                    activeIndex === i 
                                        ? "w-10 h-2.5 bg-gradient-to-r from-orange-500 to-orange-400 shadow-lg shadow-orange-500/50" 
                                        : "w-2.5 h-2.5 bg-white/70 hover:bg-white hover:scale-125"
                                } disabled:cursor-not-allowed`}
                                aria-label={`ไปยังสไลด์ ${i + 1}`}
                                aria-current={activeIndex === i ? "true" : "false"}
                            />
                        ))}
                    </div>

                    {/* Decorative Theme Badge - Hidden on Mobile */}
                    <div className="hidden md:block absolute top-6 left-6 pointer-events-none z-20">
                        <div className="flex items-center gap-2.5 bg-gradient-to-r from-blue-600 to-blue-700 backdrop-blur-md px-4 py-2 rounded-xl border border-blue-400/50 shadow-xl shadow-blue-900/30">
                            <div className="relative">
                                <div className="w-2.5 h-2.5 bg-orange-400 rounded-full animate-pulse" />
                                <div className="absolute inset-0 w-2.5 h-2.5 bg-orange-400 rounded-full animate-ping" />
                            </div>
                            <span className="text-[10px] font-black text-white uppercase tracking-[0.15em]">Medical News</span>
                        </div>
                    </div>

                    {/* Slide Counter - Hidden on Mobile */}
                    <div className="hidden md:block absolute top-6 right-6 pointer-events-none z-20">
                        <div className="bg-black/30 backdrop-blur-md px-4 py-2 rounded-xl border border-white/20 shadow-xl">
                            <span className="text-white text-sm font-bold">
                                {activeIndex + 1} / {data.length}
                            </span>
                        </div>
                    </div>
                </div>

                {/* SEO Text (Hidden but crawlable) */}
                <div className="sr-only">
                    <h2>ข่าวสารและกิจกรรม ราชพฤกษ์ไตเทียม</h2>
                    <p>ติดตามข่าวสาร กิจกรรม และความเคลื่อนไหวของศูนย์ฟอกเลือดด้วยเครื่องไตเทียม ราชพฤกษ์</p>
                </div>
            </div>
        </section>
    );
};

export default CarouselSlide;
